// Imports
import React from 'react';
import useCachedState from '../../../shareables/foundation/front-end/utils/use-cached-state';
import { GetWebsitesResponse } from '../../../shareables/types/api/main/self/websites/get';
import planMapping from '../../../shareables/utils/billing/plan-mapping';
import UpgradePage from '../../pages/upgrade';


// Map page type ID to minimum plan requirement and name
interface PageTypeRequirement {
	requiredPlan: number;
	pageTypeName: string;
}

export const pageTypePlanRequirements: Record<number, PageTypeRequirement> = {
	200: { requiredPlan: planMapping.free, pageTypeName: 'general text' },
	201: { requiredPlan: planMapping.free, pageTypeName: 'documents' },
	202: { requiredPlan: planMapping.free, pageTypeName: 'calendar' },
	203: { requiredPlan: planMapping.free, pageTypeName: 'board members' },
	204: { requiredPlan: planMapping.free, pageTypeName: 'news articles' },
	209: { requiredPlan: planMapping.free, pageTypeName: 'committees' },
	
	205: { requiredPlan: planMapping.inform, pageTypeName: 'location (map)' },
	206: { requiredPlan: planMapping.inform, pageTypeName: 'photo gallery' },
	207: { requiredPlan: planMapping.inform, pageTypeName: 'resident directory' },
	208: { requiredPlan: planMapping.inform, pageTypeName: 'pet directory' },
	211: { requiredPlan: planMapping.inform, pageTypeName: 'FAQs' },
	214: { requiredPlan: planMapping.inform, pageTypeName: 'community facilities (amenities)' },
	217: { requiredPlan: planMapping.inform, pageTypeName: 'useful contact information' },
	
	210: { requiredPlan: planMapping.deluxe, pageTypeName: 'contact form' },
	212: { requiredPlan: planMapping.deluxe, pageTypeName: 'garage sales' },
	213: { requiredPlan: planMapping.deluxe, pageTypeName: 'recipes' },
	215: { requiredPlan: planMapping.deluxe, pageTypeName: 'forum' },
	216: { requiredPlan: planMapping.deluxe, pageTypeName: 'classifieds' },
	218: { requiredPlan: planMapping.deluxe, pageTypeName: 'custom form' },
	219: { requiredPlan: planMapping.deluxe, pageTypeName: 'resident alerts' },
	220: { requiredPlan: planMapping.deluxe, pageTypeName: 'survey results' },
};


// Define the accepted props
type RequireSufficientPlanProps =
	| ({
			pageTypeID: number;
			requiredPlan?: never;
			description?: never;
			plural?: never;
	  } & React.PropsWithChildren)
	| ({
			requiredPlan: number;
			pageTypeID?: never;
			description: string;
			plural?: boolean;
	  } & React.PropsWithChildren);


// Function component
const RequireSufficientPlan: React.FC<RequireSufficientPlanProps> = ({ pageTypeID, children, plural, ...props }) => {
	// Use state
	const [website] = useCachedState<GetWebsitesResponse[0] | null>(null, 'website');
	
	
	// Initialize
	const requiredPlan = props.requiredPlan ?? pageTypePlanRequirements[pageTypeID ?? 0].requiredPlan;
	const description = props.description ?? `The “${pageTypePlanRequirements[pageTypeID ?? 0].pageTypeName}” page type`;
	
	
	// Handle insufficient plan
	if ((website?.plan ?? planMapping.free) < requiredPlan) {
		return (
			<UpgradePage
				description={description}
				requiredPlan={requiredPlan === planMapping.deluxe ? planMapping.engage : requiredPlan}
				plural={plural}
			/>
		);
	}
	
	
	// Return JSX
	return <React.Fragment>{children}</React.Fragment>;
};

export default RequireSufficientPlan;
